'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { rentalsApi, type RentalCar } from '@/lib/api/rentals';
import apiClient from '@/lib/api/client';
import { CalendarDays, CarFront, Fuel, GaugeCircle, MapPin, Search, Users, Plus } from 'lucide-react';
import { useCountry } from '@/context/CountryContext';
import { getCurrencyLabel } from '@/lib/currency';

type Option = { code: string; name: string; name_ar?: string };

function asArray(payload: any): any[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
}

export default function RentalsPage() {
  const { selectedCountry, selectedCountryCode } = useCountry();
  const currencyLabel = getCurrencyLabel(selectedCountry, selectedCountryCode);
  const [cars, setCars] = useState<RentalCar[]>([]);
  const [loading, setLoading] = useState(true);
  const [makes, setMakes] = useState<Option[]>([]);
  const [types, setTypes] = useState<Option[]>([]);

  const [filters, setFilters] = useState({
    search: '',
    city: '',
    brand: '',
    type: '',
    start_date: '',
    end_date: '',
    price_min: '',
    price_max: '',
  });

  const hasDateFilter = Boolean(filters.start_date && filters.end_date);

  const activeFilters = useMemo(() => {
    const params: Record<string, string | number> = {};
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.city.trim()) params.location = filters.city.trim();
    if (filters.brand) params.brand = filters.brand;
    if (filters.type) params.type = filters.type;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.price_min) params.price_min = Number(filters.price_min);
    if (filters.price_max) params.price_max = Number(filters.price_max);
    return params;
  }, [filters]);

  const fetchMeta = async () => {
    try {
      const [makesRes, typesRes] = await Promise.all([
        apiClient.get('/marketplace/makes/'),
        apiClient.get('/marketplace/types/'),
      ]);
      const makesRows = asArray((makesRes as any).data ?? makesRes);
      const typeRows = asArray((typesRes as any).data ?? typesRes);
      setMakes(makesRows.map((m: any) => ({ code: String(m.code || m.id), name: m.name || '', name_ar: m.name_ar || '' })));
      setTypes(typeRows.map((t: any) => ({ code: String(t.code || t.id), name: t.name || '', name_ar: t.name_ar || '' })));
    } catch {
      setMakes([]);
      setTypes([]);
    }
  };

  const fetchCars = async () => {
    setLoading(true);
    try {
      const res = await rentalsApi.getCars(activeFilters);
      setCars(asArray(res) as RentalCar[]);
    } catch {
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters.search, activeFilters.location, activeFilters.brand, activeFilters.type, activeFilters.start_date, activeFilters.end_date, activeFilters.price_min, activeFilters.price_max]);

  return (
    <div className="min-h-screen bg-slate-50 relative">
      <Navigation title="تأجير السيارات" />
      
      {/* Floating Registration Button */}
      <div className="fixed bottom-6 left-6 z-50">
          <Link href="/showroom/register">
              <Button className="rounded-full h-14 px-6 gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-xl shadow-blue-600/20 transition-transform hover:scale-105 active:scale-95">
                  <Plus className="h-4 w-4 text-orange-500" />
                  تسجيل معرض سيارات
              </Button>
          </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-24 pt-6 space-y-5">
        <Card className="rounded-3xl border-none shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-black text-slate-800">احجز سيارة للإيجار بسهولة</h1>
              <Link href="/rentals/bookings">
                <Button className="rounded-xl h-9">حجوزاتي</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2 relative">
                <Search className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  placeholder="ابحث باسم السيارة أو الوكالة"
                  className="rounded-xl pr-10"
                />
              </div>
              <Input
                value={filters.city}
                onChange={(e) => setFilters((p) => ({ ...p, city: e.target.value }))}
                placeholder="المدينة"
                className="rounded-xl"
              />
              <Select value={filters.brand || '__all'} onValueChange={(v) => setFilters((p) => ({ ...p, brand: v === '__all' ? '' : v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="الماركة" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">كل الماركات</SelectItem>
                  {makes.map((m) => <SelectItem key={m.code} value={m.code}>{m.name_ar || m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Select value={filters.type || '__all'} onValueChange={(v) => setFilters((p) => ({ ...p, type: v === '__all' ? '' : v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="النوع" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">كل الأنواع</SelectItem>
                  {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name_ar || t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" min={0} placeholder="سعر أدنى/يوم" className="rounded-xl" value={filters.price_min} onChange={(e) => setFilters((p) => ({ ...p, price_min: e.target.value }))} />
              <Input type="number" min={0} placeholder="سعر أعلى/يوم" className="rounded-xl" value={filters.price_max} onChange={(e) => setFilters((p) => ({ ...p, price_max: e.target.value }))} />
              <Input type="date" className="rounded-xl" value={filters.start_date} onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))} />
              <Input type="date" className="rounded-xl" value={filters.end_date} onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))} />
              <Button variant="outline" className="rounded-xl" onClick={() => setFilters({
                search: '', city: '', brand: '', type: '', start_date: '', end_date: '', price_min: '', price_max: '',
              })}>إعادة ضبط</Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-9 h-9 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : cars.length === 0 ? (
          <Card className="rounded-3xl border-none shadow-sm">
            <CardContent className="py-16 text-center">
              <CarFront className="h-14 w-14 text-slate-300 mx-auto mb-3" />
              <p className="font-bold text-slate-700">لا توجد سيارات إيجار متاحة حالياً</p>
              <p className="text-sm text-slate-500 mt-1">جرّب تغيير التواريخ أو الفلاتر.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cars.map((car) => {
              const href = hasDateFilter
                ? `/rentals/${car.id}?start_date=${filters.start_date}&end_date=${filters.end_date}`
                : `/rentals/${car.id}`;
              return (
                <Link key={car.id} href={href}>
                  <Card className="h-full rounded-3xl border-none shadow-sm hover:shadow-md transition-all cursor-pointer">
                    <CardContent className="p-0">
                      <div className="h-44 rounded-t-3xl overflow-hidden bg-slate-100">
                        {car.primary_image_url ? (
                          <img src={car.primary_image_url} alt={car.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <CarFront className="h-12 w-12" />
                          </div>
                        )}
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="font-black text-slate-800 line-clamp-1">{car.title}</h3>
                            <p className="text-xs text-slate-500">{car.vendor_store_name || 'مكتب تأجير'}</p>
                          </div>
                          <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold">متاح</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {car.city || '—'}</div>
                          <div className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {car.seats || 4} مقاعد</div>
                          <div className="flex items-center gap-1"><GaugeCircle className="h-3.5 w-3.5" /> {car.transmission === 'manual' ? 'عادي' : 'أوتوماتيك'}</div>
                          <div className="flex items-center gap-1"><Fuel className="h-3.5 w-3.5" /> {car.fuel_type || 'petrol'}</div>
                        </div>

                        {hasDateFilter && (
                          <div className="text-[11px] text-indigo-700 font-bold bg-indigo-50 rounded-lg px-2 py-1 inline-flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            من {filters.start_date} إلى {filters.end_date}
                          </div>
                        )}

                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-xs text-slate-500">السعر اليومي</p>
                            <p className="text-lg font-black text-blue-600">{car.daily_price} {currencyLabel}</p>
                          </div>
                          <Button size="sm" className="rounded-xl">عرض التفاصيل</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
